/**
 * MentionDropdown Component
 *
 * Displays a dropdown of agents when user types @ in the textarea.
 * Features:
 * - Triggers on @ key press
 * - Filters agents as user types
 * - Keyboard navigation (↑↓ arrows, Enter to select, Esc to close)
 * - Intelligent positioning
 * - Fetches agents internally using useAgents hook (no need to pass as props)
 */

import { Bot } from "lucide-react"
import { useEffect, useRef, useState, useMemo } from "react"
import { createPortal } from "react-dom"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import type { AgentContextItem } from "@/types/agent-context"
import { agentToContextItem } from "@/types/agent-context"
import { useAgents } from "@/hooks/use-agents"
import { useAnchoredMenuPosition } from "@/hooks/use-anchored-menu-position"
import { useScrollActiveIntoView } from "@/hooks/use-scroll-active-into-view"
import { cn } from "@/lib/utils"

type MentionDropdownProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (item: AgentContextItem) => void
  searchQuery: string
  anchorElement: HTMLElement | null
}

/**
 * MentionDropdown component for agent selection
 * fetches agents internally instead of requiring them as props
 */
export function MentionDropdown({
  open,
  onOpenChange,
  onSelect,
  searchQuery,
  anchorElement,
}: MentionDropdownProps) {
  const [filteredAgents, setFilteredAgents] = useState<AgentContextItem[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [mounted, setMounted] = useState(false)

  // Collision-aware floating position (flips/clamps so it never goes off-screen).
  const position = useAnchoredMenuPosition({
    anchorElement,
    menuRef: dropdownRef,
    open,
  })

  /**
   * - Fetch agents internally using useAgents hook
   * - Automatically fetches when dropdown opens
   * - Replaces the need to pass agents as props
   * - Uses same agent fetching logic as AgentSelector
   */
  const { state: agentsState } = useAgents({
    autoFetch: true,
    initialParams: { limit: 100 },
  })

  /**
   * - Convert fetched agents to context items
   * - Transforms Agent[] to AgentContextItem[] format
   * - Memoized to prevent unnecessary recalculations
   */
  const availableAgentContextItems = useMemo(() => {
    return agentsState.agents.map((agent) => agentToContextItem(agent))
  }, [agentsState.agents])

  // Set mounted state
  useEffect(() => {
    setMounted(true)
  }, [])

  /**
   * - Filter agents based on search query
   * - Now uses availableAgentContextItems from internal fetch
   * - Filters by agent name (case-insensitive)
   */
  useEffect(() => {
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase()
      const filtered = availableAgentContextItems.filter((agent) =>
        agent.name.toLowerCase().includes(lowerQuery)
      )
      setFilteredAgents(filtered)
    } else {
      setFilteredAgents(availableAgentContextItems)
    }
    setSelectedIndex(0)
  }, [searchQuery, availableAgentContextItems])

  // Handle keyboard navigation
  useEffect(() => {
    if (!open) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault()
        event.stopPropagation()
        setSelectedIndex((prev) =>
          prev < filteredAgents?.length - 1 ? prev + 1 : prev
        )
      } else if (event.key === "ArrowUp") {
        event.preventDefault()
        event.stopPropagation()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev))
      } else if (event.key === "Enter") {
        event.preventDefault()
        event.stopPropagation()
        if (filteredAgents[selectedIndex]) {
          onSelect(filteredAgents[selectedIndex])
          onOpenChange(false)
        }
      } else if (event.key === "Escape") {
        event.preventDefault()
        event.stopPropagation()
        onOpenChange(false)
      }
    }

    window.addEventListener("keydown", handleKeyDown, true)
    return () => window.removeEventListener("keydown", handleKeyDown, true)
  }, [open, filteredAgents, selectedIndex, onSelect, onOpenChange])

  // Keep the keyboard-focused row visible as the user arrows through the list.
  useScrollActiveIntoView(listRef, selectedIndex, open)

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        anchorElement &&
        !anchorElement.contains(event.target as Node)
      ) {
        onOpenChange(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open, onOpenChange, anchorElement])

  const handleItemClick = (agent: AgentContextItem) => {
    onSelect(agent)
    onOpenChange(false)
  }

  if (!open || !mounted) return null

  /**
   * Show loading state while agents are being fetched
   */
  const isLoading = agentsState.loading

  // Id of the keyboard-selected row, used to drive both the highlight
  // (Command `value`) and the scroll marker (`data-active`) below.
  const activeId = filteredAgents[selectedIndex]?.id ?? ""

  const dropdown = (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] w-[340px] overflow-hidden rounded-lg border border-gray-200 bg-popover text-popover-foreground shadow-lg shadow-gray-200/60"
      style={{ top: position.top, left: position.left }}
    >
      <Command
        className="rounded-lg border-none bg-transparent"
        // Keep cmdk's own selection in lockstep with our manual selectedIndex,
        // otherwise cmdk's stuck first-row highlight competes with ours.
        value={activeId}
        onValueChange={(value) => {
          const nextIndex = filteredAgents.findIndex((agent) => agent.id === value)
          if (nextIndex >= 0) {
            setSelectedIndex(nextIndex)
          }
        }}
      >
        <CommandList className="max-h-[280px] p-1" ref={listRef}>
          {isLoading && (
            <div className="flex items-center justify-center py-6 text-[13px] text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="size-4 animate-spin rounded-full border-2 border-muted-foreground border-t-foreground" />
                Loading agents...
              </div>
            </div>
          )}

          {!isLoading && filteredAgents.length === 0 && (
            <CommandEmpty>No agents found.</CommandEmpty>
          )}

          {!isLoading && filteredAgents.length > 0 && (
            <CommandGroup className="p-0">
              {filteredAgents.map((agent) => (
                <CommandItem
                  className={cn(
                    "flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] text-foreground transition-colors",
                    "aria-selected:bg-gray-100 aria-selected:text-foreground"
                  )}
                  data-active={agent.id === activeId}
                  key={agent.id}
                  value={agent.id}
                  onMouseDown={(event) => event.preventDefault()}
                  onSelect={() => handleItemClick(agent)}
                >
                  <Bot className="size-4 shrink-0 text-muted-foreground" />
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate font-medium leading-4">
                      {agent.name}
                    </span>
                    {agent.description && (
                      <span className="truncate text-[11.5px] leading-4 text-muted-foreground">
                        {agent.description}
                      </span>
                    )}
                  </div>
                  {agent.icon && (
                    <span className="shrink-0 text-base leading-none">{agent.icon}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    </div>
  )

  return createPortal(dropdown, document.body)
}